import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { Box, Button, Chip, Snackbar, Stack, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import { Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom';
import TeacherGlobalHeader from '../component/TeacherGlobalHeader';
import ClassLessonsPage from '../page/ClassLessonsPage';
import ClassOverviewPage from '../page/ClassOverviewPage';
import ClassSettingsPage from '../page/ClassSettingsPage';
import ClassStudentsPage from '../page/ClassStudentsPage';
import { classProfiles, classTabs, ClassTab, lessons } from '../page/ClassPageData';
import { aiButtonSx, BLUE, BORDER, INTER, NAVY, PAGE_BACKGROUND, POPPINS, SLATE, SUCCESS_SOFT, SUCCESS_TEXT } from '../teacherStyles';

export default function ClassLayout() {
    const history = useHistory();
    const { classId, classTab } = useParams() as { classId: string; classTab?: string };
    const profile = classProfiles[classId as keyof typeof classProfiles] ?? classProfiles['intermediate-mandarin-ii'];
    const activeTab = classTabs.includes(classTab as ClassTab) ? classTab as ClassTab : 'overview';
    const [message, setMessage] = React.useState<string | null>(null);

    const classBasePath = `/mockup/teacher/class/${classId}`;
    const createLesson = () => history.push(`${classBasePath}/lesson/new`);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: PAGE_BACKGROUND }}>
            <TeacherGlobalHeader onNotice={setMessage} />

            <Box sx={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
                <Box sx={{ maxWidth: 1220, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, pt: { xs: 2.5, md: 3.5 } }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'flex-start' }} justifyContent="space-between" spacing={2}>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 24, md: 29 }, color: NAVY, letterSpacing: '-0.025em' }}>{profile.name}</Typography>
                                <Chip label="Active" size="small" sx={{ fontFamily: INTER, fontWeight: 700, color: SUCCESS_TEXT, backgroundColor: SUCCESS_SOFT }} />
                            </Stack>
                            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.55 }}>{profile.students} students · {profile.schedule} · {profile.room}</Typography>
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button startIcon={<ContentCopyRoundedIcon />} onClick={() => setMessage('Class signup link copied.')} sx={{ fontFamily: INTER, fontWeight: 700, color: BLUE, textTransform: 'none' }}>Copy signup link</Button>
                            <Button variant="contained" startIcon={<AutoAwesomeRoundedIcon />} onClick={createLesson} sx={{ ...aiButtonSx, borderRadius: '11px', px: 2.25 }}>Create lesson</Button>
                        </Stack>
                    </Stack>

                    <Tabs value={activeTab} onChange={(_event, value: ClassTab) => history.push(`${classBasePath}/${value}`)} variant="scrollable" scrollButtons="auto" sx={{ mt: 2.5, minHeight: 44, '& .MuiTab-root': { minHeight: 44, minWidth: 0, mr: { xs: 2.25, sm: 3.5 }, px: 0, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, textTransform: 'none', color: SLATE }, '& .Mui-selected': { color: `${BLUE} !important` }, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: BLUE } }}>
                        <Tab value="overview" label="Overview" />
                        <Tab value="lesson" label="Lessons" />
                        <Tab value="student" label="Students" />
                        <Tab value="settings" label="Settings" />
                    </Tabs>
                </Box>
            </Box>

            <Box component="main" sx={{ width: '100%', maxWidth: 1220, mx: 'auto', p: { xs: 2, sm: 3, lg: 4 } }}>
                <Switch>
                    <Route
                        exact
                        path="/mockup/teacher/class/:classId/overview"
                        render={() => (
                            <ClassOverviewPage
                                className={profile.name}
                                lesson={profile.lesson}
                                studentCount={profile.students}
                                lessonCount={lessons.length}
                                onOpenLesson={() => history.push(`${classBasePath}/lesson/${profile.lessonId}`)}
                                onReviewSpeaking={() => history.push(`${classBasePath}/lesson/${profile.lessonId}/speaking-review`)}
                            />
                        )}
                    />
                    <Route exact path="/mockup/teacher/class/:classId/student" render={() => <ClassStudentsPage onAction={setMessage} />} />
                    <Route exact path="/mockup/teacher/class/:classId/lesson" render={() => <ClassLessonsPage onOpenLesson={lessonId => history.push(`${classBasePath}/lesson/${lessonId}`)} onAction={setMessage} />} />
                    <Route exact path="/mockup/teacher/class/:classId/settings" render={() => <ClassSettingsPage className={profile.name} onAction={setMessage} />} />
                    <Redirect to={`${classBasePath}/overview`} />
                </Switch>
            </Box>

            <Snackbar open={Boolean(message)} autoHideDuration={2600} onClose={() => setMessage(null)} message={message} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} ContentProps={{ sx: { borderRadius: '12px', backgroundColor: NAVY, fontFamily: INTER, fontSize: 13 } }} />
        </Box>
    );
}
